import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Typeahead} from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import {useDispatch} from 'react-redux';
import {BaseComponent} from 'web/components/BaseComponent';
import {useCaseApply} from 'web/modules/case-apply/hooks/useCaseApply';
import enumList from 'web/utils/enumList';
import {showMessage} from 'web/utils/modalHelper';

const CaseApplyPage = (props) => {
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState({
    casePriority: enumList.casePriority.normal,
    caseType: '',
    createTime: new Date().toISOString().split('T')[0],
    customerName: '',
    uploadFiles: [],
    purpose: '',
    description: '',
  });

  const {setSelectedCustomerData, onOpenChange} = props;

  const [uploadFiles, setUploadFiles] = useState('');
  const [modalConditions, setModalConditions] = useState([]);

  const [relCustomerMachineSelection, setRelCustomerMachineSelection] =
    useState([]);
  const [modalRelCustomerMachines, setModalRelCustomerMachines] = useState([]);

  const [modalAppearances, setModalAppearances] = useState([]);
  const [isRequiredAppearances, setIsRequiredAppearances] = useState(false);
  const [invalidShow, setInvalidShow] = useState('none');
  const [customerData, setCustomerData] = useState([]);
  const {
    getCustomerSelection,
    handleAddCase,
    getConditionSelection,
    handleGenerateCaseNumber,
    getRelCustomerMachineByCustomerCode,
    setCaseNumber,
    checkCoordinatesInfo,
    customerSelection,
    conditionSelection,
    appearanceSelection,
    caseNumber,
    getAppearanceList,
    goback,
  } = useCaseApply(dispatch, showMessage, setSelectedCustomerData);

  const updateFormValues = (updatedValues) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      ...updatedValues,
    }));
  };

  const handleChange = (event) => {
    const {name, value} = event.target;

    updateFormValues({
      [name]: value,
    });
  };

  const handleCaseTypeChange = (event) => {
    let {value} = event.target;
    value = Number(value);
    const {purpose} = formValues;

    // 提取 purpose 的前綴和內容
    const [prevPrefix, ...purposeContentParts] = purpose.split(':');
    const purposeContent = purposeContentParts.join(':').trim();

    // 處理清空 + repair 類型
    if (value === enumList.CaseTypeEnums.repair.value) {
      return updateFormValues({
        caseType: value,
        purpose: purposeContent,
      });
    }

    const caseTypes = Object.values(enumList.CaseTypeEnums);

    // 獲取新 case type 名稱
    const caseTypeName = caseTypes.find(
      ({value: v}) => v === value,
    )?.stringValue;

    // 判斷是否需要替換前綴
    const shouldReplacePurpose = caseTypes.some(
      ({stringValue: content}) => content === prevPrefix,
    );

    const newPurpose = shouldReplacePurpose
      ? `${caseTypeName}:${purposeContent || ''}`
      : `${caseTypeName}:${purpose}`;

    updateFormValues({
      caseType: value,
      purpose: newPurpose,
    });
  };

  const handleConditionChange = (event) => {
    const {value} = event.target;

    if (modalConditions.includes(value)) {
      const filteredConditions = modalConditions.filter(
        (item) => item !== value,
      );
      setModalConditions(filteredConditions);
      const filteredAppearances = modalAppearances.filter((appearance) => {
        const skills = appearance.skills;
        return skills.some((skill) =>
          filteredConditions.includes(skill.condition_code),
        );
      });
      setModalAppearances(filteredAppearances);
    } else {
      setModalConditions([...modalConditions, value]);
    }
  };

  const handleUploadChange = (event) => {
    const {files} = event.target;
    const file = files[0];
    setUploadFiles([...uploadFiles, file]);
  };

  const handleCustomerChange = async (selected) => {
    setCustomerData(selected);
  };

  const handleMachineCheckboxChange = (event) => {
    let {value} = event.target;
    value = parseInt(value);
    if (modalRelCustomerMachines.includes(value)) {
      setModalRelCustomerMachines(
        modalRelCustomerMachines.filter((item) => item !== value),
      );
    } else {
      setModalRelCustomerMachines([...modalRelCustomerMachines, value]);
    }
  };

  const deleteUploadFile = (index) => {
    const newFiles = uploadFiles.filter((file, i) => i !== index);
    setUploadFiles(newFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;

    const missingAppearanceData =
      isRequiredAppearances &&
      modalAppearances.length === 0 &&
      modalConditions.length === 0;

    if (
      form.checkValidity() === false ||
      missingAppearanceData ||
      modalRelCustomerMachines.length === 0
    ) {
      setInvalidShow('block');
      return;
    }
    form.classList.add('was-validated');

    // 建立 FormData
    const formData = new FormData(form);
    const req = {
      casePriority: formData.get('casePriority'),
      caseType: formData.get('caseType'),
      caseNumber: caseNumber,
      createTime: formValues.createTime,
      customerCode: customerData[0].customerCode,
      description: formData.get('description'),
      purpose: formData.get('purpose'),
      conditions: modalConditions,
      appearances: modalAppearances.map(({id}) => id),
      machineIds: modalRelCustomerMachines,
    };
    const files = uploadFiles;

    // 判定經緯度是否填寫
    if (!(await checkCoordinatesInfo(customerData[0].customerCode))) {
      return onOpenChange(true);
    }

    await handleAddCase({caseData: req, files});

    resetForm(form);
    goback();
  };

  const resetForm = (form) => {
    form.classList.remove('was-validated');
    setUploadFiles([]);
    setCustomerData([]);
    setModalConditions([]);
    setModalAppearances([]);
    setRelCustomerMachineSelection([]);
    setModalRelCustomerMachines([]);
    setFormValues({
      casePriority: enumList.casePriority.normal,
      caseType: '',
      caseNumber: '',
      createTime: new Date().toISOString().split('T')[0],
      customerName: '',
      description: '',
      // handlingProcess: '',
    });
    handleGenerateCaseNumber();
  };

  const handleAppearanceChange = (selectes) => {
    console.log(selectes);
    // 判斷該操作新增還是移除
    if (selectes.length > modalAppearances.length) {
      const currentSelected = selectes.find(
        (select) => !modalAppearances.includes(select),
      );
      loadConditionsByAppearanceType(currentSelected);
    } else {
      const currentSelected = modalAppearances.find(
        (select) => !selectes.includes(select),
      );
      unloadConditionsByAppearance(currentSelected);
    }
    setModalAppearances(selectes);
  };

  const unloadConditionsByAppearance = (currentSelected) => {
    const clearSelection = currentSelected.skills
      .map((item) => item.condition_code)
      .flat();
    setModalConditions((prev) =>
      prev.filter((condition) => !clearSelection.includes(condition)),
    );
  };

  const loadConditionsByAppearanceType = (currentSelected) => {
    const appearanceSelection = currentSelected.skills
      .map((item) => item.condition_code)
      .flat();

    const nextState = [...modalConditions, ...appearanceSelection];
    setModalConditions([...new Set(nextState)]);
  };

  const availableSkills = useMemo(() => {
    // 渲染指派條件用，讓UI不那麼亂
    return modalAppearances.map((condition) => condition.skills).flat();
  }, [modalAppearances, modalConditions]);

  const updateCustomerData = useCallback(async () => {
    const {customerCode} = customerData?.[0] ?? {};
    if (!customerCode) {
      setRelCustomerMachineSelection([]);
      setModalRelCustomerMachines([]);
      return;
    }
    const machineList = await getRelCustomerMachineByCustomerCode(customerCode);
    const sortedMachineList = machineList.toSorted(
      (a, b) => a.machineTypeId - b.machineTypeId,
    );
    setRelCustomerMachineSelection(sortedMachineList);
    setModalRelCustomerMachines([]);
  }, [customerData]);

  useEffect(() => {
    updateCustomerData();
  }, [customerData]);

  useEffect(() => {
    const baseComponent = new BaseComponent(props);
    baseComponent.checkPermission(
      enumList.moduleEnums.caseApply,
      enumList.accessModeEnums.read,
    );
    getCustomerSelection();
    getConditionSelection();
    handleGenerateCaseNumber();
    getAppearanceList();
  }, []);

  useEffect(() => {
    const caseType = parseInt(formValues.caseType);
    const requiredCaseTypes = [
      enumList.CaseTypeEnums.repair.value,
      enumList.CaseTypeEnums.maintenance.value,
    ];
    if (requiredCaseTypes.includes(caseType)) {
      return setIsRequiredAppearances(true);
    }
    setIsRequiredAppearances(false);
  }, [formValues.caseType]);

  return (
    <section id="datatable-container">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <svg
              className="nav__icon nav__icon--breadcrumb me-3"
              aria-hidden="true"
            >
              <use href="#ico-home-line"></use>
            </svg>
            AI智能派工
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            立案
          </li>
        </ol>
      </nav>
      <h4>立案</h4>
      <form
        className=" needs-validation"
        id="modalResidentForm"
        noValidate
        onSubmit={handleSubmit}
      >
        <div className="modal-body">
          <div className="d-flex flex-column gap-3 px-2">
            <div className="modal__form__catagory row">
              <div className="col-12">
                <div className="row">
                  <div className="col">
                    <label
                      htmlFor="casePriority"
                      className="form-label me-auto"
                    >
                      案件等級
                    </label>
                    <div
                      className="btn-group btn-group-sm w-100"
                      role="group"
                      aria-label="radio toggle button group"
                    >
                      <input
                        type="radio"
                        className="btn-check"
                        name="casePriority"
                        id="priorityNormal"
                        autoComplete="off"
                        value={enumList.casePriority.normal}
                        checked={
                          Number(formValues.casePriority) ===
                          enumList.casePriority.normal
                        }
                        onChange={handleChange}
                        data-checkbox-group="required"
                      />
                      <label
                        className="btn btn-outline-secondary"
                        htmlFor="priorityNormal"
                      >
                        一般
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="casePriority"
                        id="priorityHigh"
                        autoComplete="off"
                        value={enumList.casePriority.high}
                        checked={
                          Number(formValues.casePriority) ===
                          enumList.casePriority.high
                        }
                        onChange={handleChange}
                        data-checkbox-group="required"
                      />
                      <label
                        className="btn btn-outline-secondary"
                        htmlFor="priorityHigh"
                      >
                        <svg className="btn__icon" aria-hidden="true">
                          <use href="#ico-zap"></use>
                        </svg>
                        緊急
                      </label>
                    </div>
                  </div>
                  <div className="col">
                    <label htmlFor="caseType" className="form-label me-auto">
                      案件服務區分
                    </label>
                    <select
                      id="caseType"
                      name="caseType"
                      className="form-select form-select-sm"
                      value={formValues.caseType}
                      onChange={handleCaseTypeChange}
                      required
                    >
                      <option value="" disabled>
                        請選擇
                      </option>
                      {Object.values(enumList.CaseTypeEnums).map(
                        ({value, stringValue}) => (
                          <option key={value} value={value}>
                            {stringValue}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <div className="col">
                    <label htmlFor="caseNumber" className="form-label">
                      立案編號
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      id="caseNumber"
                      name="caseNumber"
                      value={caseNumber}
                      required
                      disabled
                    />
                  </div>
                  <div className="col">
                    <label htmlFor="createTime" className="form-label">
                      立案時間
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      id="createTime"
                      name="createTime"
                      value={formValues.createTime}
                      onChange={handleChange}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <label htmlFor="customerName" className="form-label">
                      客戶名稱
                    </label>
                    <Typeahead
                      id="customerName"
                      size="sm"
                      onChange={handleCustomerChange}
                      selected={customerData}
                      inputProps={{required: true}}
                      value={customerData}
                      options={customerSelection}
                      renderInput={({
                        inputRef,
                        referenceElementRef,
                        ...inputProps
                      }) => (
                        <input
                          {...inputProps}
                          className="rbt-input-main form-control rbt-input form-control-sm form-select"
                          ref={(node) => {
                            inputRef(node);
                            referenceElementRef(node);
                          }}
                        />
                      )}
                      labelKey="customerName"
                      placeholder="請輸入客戶名稱"
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="uploadFiles" className="form-label">
                      上傳附件
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      id="uploadFiles"
                      name="uploadFiles"
                      value=""
                      onChange={handleUploadChange}
                      accept=".pdf"
                    ></input>
                    <div className="d-flex gap-2 flex-wrap my-2">
                      {uploadFiles.length > 0 &&
                        uploadFiles.map((file, index) => (
                          <button
                            type="button"
                            key={index}
                            className="btn btn-outline-secondary rounded-pill"
                          >
                            <svg className="btn__icon me-1" aria-hidden="true">
                              <use href="#ico-file-04"></use>
                            </svg>
                            {file.name}
                            <i
                              className="bi bi-x-lg ms-3"
                              onClick={(e) => {
                                e.preventDefault();
                                deleteUploadFile(index);
                              }}
                              style={{
                                color: 'var(--ce-colors-gray-light-mode--700)',
                              }}
                            ></i>
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
                {/* 客戶機號選擇 */}
                <div className="row">
                  <div className="col-12">
                    <label className="form-label mt-2">客戶機號</label>
                    <div className="row g-2">
                      {relCustomerMachineSelection.map((machine, index) => (
                        <div className="col-4" key={index}>
                          <input
                            type="checkbox"
                            className="btn-check"
                            id={`machine${machine.machineSerialNumber}`}
                            value={machine.machineId}
                            onChange={handleMachineCheckboxChange}
                            checked={modalRelCustomerMachines.includes(
                              machine.machineId,
                            )}
                          />
                          <label
                            className="btn btn-sm btn-outline-secondary"
                            htmlFor={`machine${machine.machineSerialNumber}`}
                          >
                            {`${machine.machineTypeName} -
                              ${machine.machineSerialNumber}`}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* 現象選擇 */}
                <div className="row">
                  <div className="col-12">
                    <label htmlFor="appearanceType" className="form-label mt-2">
                      現象選擇
                    </label>
                    <div className="row g-2">
                      <Typeahead
                        id="appearance-name"
                        size="sm"
                        onChange={handleAppearanceChange}
                        multiple
                        selected={modalAppearances}
                        options={appearanceSelection}
                        labelKey="name"
                        placeholder="請輸入現象名稱"
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <label htmlFor="modalApplyCondition" className="form-label">
                      指派條件
                    </label>
                    <div className="row g-2">
                      {modalAppearances.length > 0 &&
                        availableSkills.map((condition, index) => (
                          <div className="col-4" key={index}>
                            <input
                              type="checkbox"
                              className="btn-check"
                              name="modalApplyCondition"
                              id={`modalApplyCondition${condition.condition_code}`}
                              autoComplete="off"
                              value={condition.condition_code}
                              onChange={handleConditionChange}
                              checked={modalConditions.includes(
                                condition.condition_code,
                              )}
                            />
                            <label
                              className="btn btn-sm btn-outline-secondary"
                              htmlFor={`modalApplyCondition${condition.condition_code}`}
                            >
                              {condition.condition_name}
                            </label>
                          </div>
                        ))}
                    </div>
                  </div>
                  <div className="col-12">
                    <label htmlFor="description" className="form-label">
                      案件目的
                    </label>
                    <textarea
                      className="form-control"
                      id="purpose"
                      name="purpose"
                      value={formValues.purpose}
                      onChange={handleChange}
                      rows="2"
                    ></textarea>
                  </div>
                  <div className="col-12">
                    <label htmlFor="description" className="form-label">
                      案件內容
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      value={formValues.description}
                      onChange={handleChange}
                      rows="2"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <div
                  className="alert alert-warning global-invalid-feedback"
                  style={{display: invalidShow}}
                ></div>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end w-100">
          <button type="button" className="btn btn-cancel" onClick={goback}>
            取消
          </button>
          <button type="submit" className="btn btn-primary">
            儲存
          </button>
        </div>
      </form>
    </section>
  );
};

export default CaseApplyPage;
